import ElakayImage from '../../../components/images/ElakayImage';
// "My Connected Sellers" (Provider Center): the Seller shops that sell THIS Provider's products through
// Dropshipping In, and which of the Provider's products each shop linked. Real data only (server-computed
// stats, server-side search/pagination); reuses PageShell (real Layout, header, footer) and center.css.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { toImageUrl } from "../../../config";
import PageShell, { tokenOf } from "./PageShell";
import { listConnectedSellers, listConnectedSellerProducts } from "../services/connectedSellersService";
import { money } from "../ui/wholesaleCart";
import "./connectedSellers.css";
import GlobalRatingBadge from "../components/GlobalRatingBadge";

const PAGE_SIZE = 12;

const Thumb = ({ src, alt, round }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return src && !broken
    ? <ElakayImage preset="product-thumb" className={`cs-thumb${round ? " cs-thumb--round" : ""}`} src={toImageUrl(src)} alt={alt || ""} loading="lazy" onError={() => setBroken(true)} />
    : <span className={`cs-thumb cs-thumb--empty${round ? " cs-thumb--round" : ""}`} aria-hidden="true"><i className={`fas ${round ? "fa-store" : "fa-box"}`} /></span>;
};

// Own paginated fetch (loading / error / retry) driven by `fetcher(token)`; a stale response never overwrites a newer one.
const useServerList = (fetcher, deps) => {
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    fetcher(tokenOf()).then((r) => {
      if (!live) return;
      if (r?.error || r?.success === false) setState({ data: null, loading: false, error: r.error || "Error" });
      else setState({ data: r, loading: false, error: "" });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { ...state, reload: () => setTick((t) => t + 1) };
};

const Pager = ({ pagination, onPage }) => pagination && pagination.totalPages > 1 && (
  <nav className="cs-pager" aria-label={text("h_cs_pagination", "Pagination")}>
    <button type="button" className="ctr__btn" disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>‹ {text("h_cs_prev", "Previous")}</button>
    <span>{pagination.page} / {pagination.totalPages}</span>
    <button type="button" className="ctr__btn" disabled={pagination.page >= pagination.totalPages} onClick={() => onPage(pagination.page + 1)}>{text("h_cs_next", "Next")} ›</button>
  </nav>
);

export const ProductMini = ({ product }) => (
  <li className="cs-product">
    <Thumb src={product.ProductImageUrls?.[0]} alt={product.productName} />
    <div className="cs-product__body">
      <strong title={product.productName}>{product.productName || "—"}</strong>
      <GlobalRatingBadge ratings={product.ratings} size="11px" />
      <div className="cs-product__prices">
        <span>{text("h_cs_selling", "Selling price")}<b>{money(product.sellingPrice)}</b></span>
        <span>{text("h_cs_provider_price", "Provider price")}<b>{money(product.providerPrice)}</b></span>
      </div>
      <span className={`cs-badge cs-badge--${product.status}`}><span className="cs-badge__dot" aria-hidden="true" />{product.status === "active" ? text("h_cs_status_active", "Active") : text("h_cs_status_draft", "Draft")}</span>
    </div>
  </li>
);

export default function ConnectedSellers({ infoSource }) {
  const [page, setPage] = useState(1);
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("products");
  const load = useServerList((t) => listConnectedSellers(t, { page, limit: PAGE_SIZE, search, sort }), [page, search, sort]);
  const submit = useCallback((e) => { e.preventDefault(); setPage(1); setSearch(input.trim()); }, [input]);
  const data = load.data;
  const ov = data?.overview;
  const sellers = data?.sellers || [];

  return (
    <PageShell infoSource={infoSource} titleElakay="providerProducts" title={text("h_cs_title", "My Connected Sellers")} icon="fa-users" className="cs-page"
      subtitle={text("h_cs_sub", "Seller shops that sell your products through Dropshipping In.")} crumb={text("h_cs_title", "My Connected Sellers")}
      state={{ loading: load.loading && !data, error: load.error, reload: load.reload }}>
      {ov && (
        <section className="ctr__stats cs-stats" aria-label={text("h_cs_overview", "Overview")}>
          {[["h_cs_connected_shops", "Connected Shops", ov.connectedShops, "fa-store"], ["h_cs_linked_products", "Linked Products", ov.linkedProducts, "fa-link"], ["h_cs_unique_products", "Unique Products", ov.uniqueProducts, "fa-box-open"]].map(([k, d, v, icon]) => (
            <div className="ctr__stat cs-stat" key={k} data-testid={k}><span className="cs-stat__icon" aria-hidden="true"><i className={`fas ${icon}`} /></span><div><span>{text(k, d)}</span><strong>{Number(v).toLocaleString()}</strong></div></div>
          ))}
          {ov.pendingDeletion > 0 && <div className="ctr__stat cs-stat cs-stat--muted"><span className="cs-stat__icon" aria-hidden="true"><i className="fas fa-clock" /></span><div><span>{text("h_cs_pending_deletion", "Pending deletion")}</span><strong>{ov.pendingDeletion}</strong></div></div>}
        </section>
      )}
      <form className="cs-toolbar" onSubmit={submit} role="search">
        <div className="cs-search"><i className="fas fa-search" aria-hidden="true" /><input type="search" aria-label={text("h_cs_search", "Search Shops")} placeholder={text("h_cs_search", "Search Shops")} value={input} onChange={(e) => setInput(e.target.value)} /></div>
        <button type="submit" className="ctr__btn ctr__btn--primary">{text("h_cs_search_btn", "Search")}</button>
        <select aria-label={text("h_cs_sort", "Sort by")} value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }}>
          <option value="products">{text("h_cs_sort_products", "Most linked products")}</option>
          <option value="name">{text("h_cs_sort_name", "Shop name")}</option>
          <option value="recent">{text("h_cs_sort_recent", "Recently connected")}</option>
        </select>
      </form>
      {!sellers.length ? (
        <div className="ctr__empty cs-empty"><i className={`fas ${search ? "fa-search" : "fa-store"}`} aria-hidden="true" /><p>{search ? text("h_cs_no_match", "No shop matches your search.") : text("h_cs_none", "No Connected Sellers Yet.")}</p></div>
      ) : (
        <div className="cs-grid" aria-busy={load.loading}>
          {sellers.map((s) => (
            <article className="cs-shop" key={s.sellerId}>
              <header className="cs-shop__head">
                <Thumb src={s.shopLogo} alt={s.shopName} round />
                <div className="cs-shop__identity"><h2>{s.shopName || "—"}</h2>
                  <span className="cs-shop__count">{text("h_cs_linked_products", "Linked Products")} : <b>{s.totalLinkedProducts}</b></span>
                  {s.firstConnectedAt && <small><i className="far fa-calendar-alt" aria-hidden="true" /> {text("h_cs_since", "Connected since")} {new Date(s.firstConnectedAt).toLocaleDateString()}</small>}</div>
              </header>
              <div className="cs-shop__actions">
                <Link className="ctr__btn" to={s.shopPath}><i className="fas fa-store" aria-hidden="true" />{text("h_cs_view_shop", "View Shop")}</Link>
                <Link className="ctr__btn ctr__btn--primary" to={`/provider/connected-sellers/${s.sellerId}`}>{text("h_cs_view_all", "View All Linked Products")}<i className="fas fa-arrow-right" aria-hidden="true" /></Link>
              </div>
              <h3>{text("h_cs_products_of", "Products linked to this Provider")}</h3>
              {s.previewProducts.length ? <ul className="cs-products">{s.previewProducts.map((p) => <ProductMini key={p.sellerProductId} product={p} />)}</ul>
                : <div className="ctr__empty">{text("h_cs_no_products", "No Linked Products")}</div>}
            </article>
          ))}
        </div>
      )}
      <Pager pagination={data?.pagination} onPage={setPage} />
    </PageShell>
  );
}

// /provider/connected-sellers/:sellerId -- every product ONE shop linked to this Provider (own pagination).
// Reloaded from the API on refresh (never depends on router state); the backend re-checks the relation.
export function ConnectedSellerProducts({ infoSource }) {
  const { sellerId } = useParams();
  const [page, setPage] = useState(1);
  const load = useServerList((t) => listConnectedSellerProducts(t, sellerId, { page, limit: PAGE_SIZE }), [sellerId, page]);
  const data = load.data;
  return (
    <PageShell infoSource={infoSource} titleElakay="providerProducts" title={data?.shop?.shopName || text("h_cs_title", "My Connected Sellers")} icon="fa-store" className="cs-page"
      crumb={text("h_cs_title", "My Connected Sellers")}
      subtitle={data ? `${text("h_cs_linked_products", "Linked Products")} : ${data.pagination.total}` : ""}
      actions={<Link className="ctr__btn" to="/provider/connected-sellers">‹ {text("h_cs_title", "My Connected Sellers")}</Link>}
      state={{ loading: load.loading && !data, error: load.error, reload: load.reload }}>
      {data && (
        <>
          <div className="cs-shop__actions cs-detail-actions"><Link className="ctr__btn" to={data.shop.shopPath}><i className="fas fa-store" aria-hidden="true" />{text("h_cs_view_shop", "View Shop")}</Link></div>
          {data.products.length ? <ul className="cs-products cs-products--full">{data.products.map((p) => <ProductMini key={p.sellerProductId} product={p} />)}</ul>
            : <div className="ctr__empty">{text("h_cs_no_products", "No Linked Products")}</div>}
          <Pager pagination={data.pagination} onPage={setPage} />
        </>
      )}
    </PageShell>
  );
}
